import axios from 'axios';
import { buildLoginPath } from '../utils/authRedirect';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';


const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor to add JWT token to headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor to handle session expiry
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            const onLoginPage = window.location.pathname.startsWith('/auth/login');
            if (!onLoginPage) {
                window.location.href = buildLoginPath();
            }
        }
        return Promise.reject(error);
    }
);

export default api;

export const authService = {
    login: (credentials) => api.post('/auth/token/', credentials),
    cognitoLogin: (idToken) => api.post('/auth/cognito/', { id_token: idToken }),
    register: (userData) => api.post('/auth/users/', userData),
    getCurrentUser: () => api.get('/auth/users/me/'),

    refreshToken: (refresh) => api.post('/auth/token/refresh/', { refresh }),
    verifyEmail: (email, otp) => api.post('/auth/verify-email/', { email, otp }),
    resendOTP: (email) => api.post('/auth/resend-otp/', { email }),
    sendOTP: (email) => api.post('/auth/users/send_otp/', { email }),
    loginWithOTP: (email, otp) => api.post('/auth/login-with-otp/', { email, otp }),
    checkAuth: () => api.get('/auth/users/check_auth/'),
    resetPassword: (userId, data) => api.post(`/auth/users/${userId}/reset_password/`, data),
    updateProfile: (data) => api.patch('/auth/users/update_profile/', data),
    switchCampus: (campusId) => api.post('/auth/users/switch_campus/', { campus: campusId }),
};

export const campusService = {
    getCampuses: () => api.get('/campuses/campuses/'),
    getCampusDetail: (id) => api.get(`/campuses/campuses/${id}/`),
    verifyRegistrationCode: (code) =>
        api.get('/campuses/campuses/verify-registration-code/', { params: { code } }),
    verifyInvite: (token, email) =>
        api.get('/campuses/campuses/verify-invite/', { params: { token, email } }),
    inviteStudent: (campusId, email) =>
        api.post(`/campuses/campuses/${campusId}/invite-student/`, { email }),
};

export const userService = {
    getUsers: (params) => api.get('/auth/users/', { params }),
    getStudents: (params) => api.get('/auth/users/students/', { params }),
    getStudentDetail: (id) => api.get(`/auth/users/${id}/`),
    approveStudent: (id) => api.post(`/auth/users/${id}/approve/`),
    rejectStudent: (id, reason) => api.post(`/auth/users/${id}/reject/`, { reason }),
    getSupportingDocuments: (userId) => api.get(`/auth/supporting-documents/?user=${userId}`),
    verifyDocument: (id) => api.post(`/auth/supporting-documents/${id}/verify/`),
    unverifyDocument: (id) => api.post(`/auth/supporting-documents/${id}/unverify/`),
    rejectDocument: (id, reason) => api.post(`/auth/supporting-documents/${id}/reject/`, reason ? { reason } : {}),
    uploadDocument: (formData) => api.post('/auth/supporting-documents/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }),
    updateStudent: (id, data) => api.patch(`/auth/users/${id}/`, data),
};

export const courseService = {
    getCourses: (params) => api.get('/courses/courses/', { params }),
    getCourseDetail: (id) => api.get(`/courses/courses/${id}/`),
    createCourse: (data) => api.post('/courses/courses/', data, {
        headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    }),
    updateCourse: (id, data) => api.patch(`/courses/courses/${id}/`, data, {
        headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    }),
    deleteCourse: (id) => api.delete(`/courses/courses/${id}/`),

    getSubjects: (params) => api.get('/courses/subjects/', { params }),
    getSubjectDetail: (id) => api.get(`/courses/subjects/${id}/`),
    createSubject: (data) => api.post('/courses/subjects/', data),
    updateSubject: (id, data) => api.patch(`/courses/subjects/${id}/`, data),
    deleteSubject: (id) => api.delete(`/courses/subjects/${id}/`),

    getLessons: (params) => api.get('/courses/lessons/', { params }),
    getLessonDetail: (id) => api.get(`/courses/lessons/${id}/`),
    createLesson: (data) => api.post('/courses/lessons/', data),
    updateLesson: (id, data) => api.patch(`/courses/lessons/${id}/`, data),
    deleteLesson: (id) => api.delete(`/courses/lessons/${id}/`),

    toggleLessonCompletion: (id) => api.post(`/courses/lessons/${id}/toggle_completion/`),
    getLessonProgress: (id) => api.get(`/courses/lessons/${id}/progress/`),
    
};

export const assessmentService = {
    getAssessments: (params) => api.get('/assessments/assessments/', { params }),
    getAssessmentDetail: (id) => api.get(`/assessments/assessments/${id}/`),
    createAssessment: (data) => api.post('/assessments/assessments/', data),
    updateAssessment: (id, data) => api.patch(`/assessments/assessments/${id}/`, data),
    deleteAssessment: (id) => api.delete(`/assessments/assessments/${id}/`),
    
    // Questions
    getQuestions: (assessmentId) => api.get(`/assessments/questions/?assessment=${assessmentId}`),
    createQuestion: (data) => api.post('/assessments/questions/', data),
    updateQuestion: (id, data) => api.patch(`/assessments/questions/${id}/`, data),
    deleteQuestion: (id) => api.delete(`/assessments/questions/${id}/`),
    
    // Question Options
    createOption: (data) => api.post('/assessments/options/', data),
    updateOption: (id, data) => api.patch(`/assessments/options/${id}/`, data),
    deleteOption: (id) => api.delete(`/assessments/options/${id}/`),

    // Submissions & Grading
    startSubmission: (assessmentId) => api.post(`/assessments/assessments/${assessmentId}/start/`),
    getCurrentSubmission: (assessmentId) => api.get(`/assessments/assessments/${assessmentId}/current-submission/`),
    submitAssessment: (assessmentId, answers) => api.post(`/assessments/submissions/submit-assessment/`, {
        assessment_id: assessmentId,
        answers
    }),
    getSubmissionResult: (id) => api.get(`/assessments/submissions/${id}/`),
    getSubmissions: (params) => api.get('/assessments/submissions/', { params }),
    submitManualGrades: (submissionId, grades) => api.post(`/assessments/submissions/${submissionId}/grade/`, { grades }),
};

export const enrollmentService = {
    getEnrollments: (params) => api.get('/enrollment/enrollments/', { params }),
    createEnrollment: (courseId) => api.post('/enrollment/enrollments/', { course: courseId }),
    getEnrollmentDetail: (id) => api.get(`/enrollment/enrollments/${id}/`),
    getEnrollmentProgress: (id) => api.get(`/enrollment/enrollments/${id}/progress/`),
    getAllEnrollments: (params) => api.get('/enrollment/enrollments/', { params }),
    approveEnrollment: (id) => api.post(`/enrollment/enrollments/${id}/approve/`),
    rejectEnrollment: (id, reason) => api.post(
        `/enrollment/enrollments/${id}/reject/`,
        reason ? { reason } : {},
    ),
    getPaymentVerificationSummary: () => api.get('/enrollment/enrollments/payment_verification_summary/'),
};

export const cohortService = {
    getCohorts: (params) => api.get('/enrollment/cohorts/', { params }),
    getCohortDetail: (id) => api.get(`/enrollment/cohorts/${id}/`),
    createCohort: (data) => api.post('/enrollment/cohorts/', data),
    updateCohort: (id, data) => api.patch(`/enrollment/cohorts/${id}/`, data),
    deleteCohort: (id) => api.delete(`/enrollment/cohorts/${id}/`),
    addStudents: (id, studentIds) => api.post(`/enrollment/cohorts/${id}/add_students/`, { student_ids: studentIds }),
    removeStudents: (id, studentIds) => api.post(`/enrollment/cohorts/${id}/remove_students/`, { student_ids: studentIds }),
    enrollStudents: (id, courseId) => api.post(`/enrollment/cohorts/${id}/enroll_students/`, { course_id: courseId }),
};

export const notificationService = {
    getNotifications: (params) => api.get('/notifications/notifications/', { params }),
    getNotification: (id) => api.get(`/notifications/notifications/${id}/`),
    getUnreadCount: () => api.get('/notifications/notifications/unread_count/'),
    getMyNotifications: (params) => api.get('/notifications/notifications/my_notifications/', { params }),
    markRead: (id) => api.post(`/notifications/notifications/${id}/mark_read/`),
    markUnread: (id) => api.post(`/notifications/notifications/${id}/mark_unread/`),
    markAllRead: () => api.post('/notifications/notifications/mark_all_read/'),
    createNotification: (data) => api.post('/notifications/notifications/', data),
    generateMessageTemplates: (data) =>
        api.post('/notifications/notifications/generate-message-templates/', data),
};

export const certificateService = {
    getCertificates: (params) => api.get('/certificates/certificates/', { params }),
    downloadCertificate: (id) => api.get(`/certificates/certificates/${id}/download/`, { responseType: 'blob' }),
};

export const analyticsService = {
    // Student
    getMyAnalytics: () => api.get('/analytics/my-analytics/'),
    trackTime: (courseId, durationMinutes) =>
        api.post('/analytics/track-time/', { course_id: courseId, duration_minutes: durationMinutes }),

    // Admin
    getAdminSummary: () => api.get('/analytics/admin-summary/'),
    exportReport: (report) =>
        api.get('/analytics/admin-export/', {
            params: { report },
            responseType: 'blob',
        }),
};
